import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global visual polish handler — mounts once, does two jobs:
 *
 * 1. Image fade-in:   Any <img> without `data-no-fade` gets `.is-loaded`
 *                     added once the browser has fully loaded it. CSS then
 *                     fades from opacity 0 → 1 in 180ms.
 *
 * 2. Section reveal:  Every <section> and every element with `data-reveal`
 *                     gets `.reveal-init` (starting state — opacity 0,
 *                     translateY 18px) at mount, then `.is-in-view` once
 *                     IntersectionObserver reports it in the viewport.
 *                     CSS transitions it to opacity 1, translateY 0 over
 *                     ~320ms easeOut.
 *
 * Rationale for one global handler rather than a wrapper component:
 *   - Works for every future page/section we build with zero developer
 *     effort — no imports, no props, no wrapper tags.
 *   - MutationObserver picks up nodes added later by React state changes.
 *   - Sections above-the-fold (top-of-page) are marked visible immediately
 *     so they never fade in from empty on page-load.
 *   - Respects `prefers-reduced-motion` completely.
 *
 * Escape hatches:
 *   - <img data-no-fade>       — image loads instantly
 *   - <section data-no-reveal> — section renders instantly
 */
const attachImage = (img) => {
  if (img.dataset.noFade !== undefined) return;
  if (img.complete && img.naturalWidth > 0) {
    img.classList.add('is-loaded');
    return;
  }
  const done = () => {
    img.classList.add('is-loaded');
    img.removeEventListener('load', done);
    img.removeEventListener('error', done);
  };
  img.addEventListener('load', done);
  img.addEventListener('error', done);
};

let sharedObserver = null;
const getObserver = () => {
  if (sharedObserver) return sharedObserver;
  sharedObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in-view');
          sharedObserver.unobserve(entry.target);
        }
      });
    },
    { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.06 },
  );
  return sharedObserver;
};

const attachReveal = (el) => {
  if (el.dataset.noReveal !== undefined) return;
  if (el.classList.contains('reveal-init') || el.classList.contains('is-in-view')) return;
  // If element is already in view at mount, skip the fade entirely — hero
  // sections and above-the-fold content must render instantly.
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  if (rect.top < vh * 0.85) {
    el.classList.add('is-in-view');
    return;
  }
  el.classList.add('reveal-init');
  getObserver().observe(el);
};

const PageAnimations = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // Sweep everything present at mount.
    document.querySelectorAll('img').forEach(attachImage);
    document.querySelectorAll('section, [data-reveal]').forEach(attachReveal);

    // Watch for additions from React re-renders / route changes.
    const mo = new MutationObserver((entries) => {
      entries.forEach((entry) => {
        entry.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'IMG') attachImage(n);
          if (n.tagName === 'SECTION' || n.dataset?.reveal !== undefined) attachReveal(n);
          n.querySelectorAll?.('img')?.forEach(attachImage);
          n.querySelectorAll?.('section, [data-reveal]')?.forEach(attachReveal);
        });
      });
    });
    mo.observe(document.body, { subtree: true, childList: true });
    return () => mo.disconnect();
  }, [pathname]);

  return null;
};

export default PageAnimations;
